import {Component, inject, signal, computed} from '@angular/core';
import {EmailService} from '../../../services/http/email.service';
import {PdfService} from '../../../services/pdf.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DataService} from '../../../services/store/data.service';
import {PredictionRotaWeek} from '../../../classes/models/PredictionRotaWeek';
import {EmailFormComponent} from './email-form/email-form.component';
import moment from 'moment';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';
import {RotaWeekDataComponent} from "../rota-week-data/rota-week-data.component";

/**
 * This component displays rota PDF contents and handles exporting rotas.
 */
@Component({
  selector: 'app-export-rota',
  templateUrl: './export-rota.component.html',
  styleUrls: ['./export-rota.component.less'],
  imports: [SHARED_IMPORTS, RotaWeekDataComponent]
})
export class ExportRotaComponent {
  private pdfService = inject(PdfService);
  private modalService = inject(NgbModal);
  dataService = inject(DataService);

  /** A number of rotas, who are loaded on the page. All rotas must be loaded in order to correctly export all rotas. */
  rotasLoaded = signal(0);

  /**
   * Returns `true` if all rotas are already loaded on the page.
   */
  allRotasLoaded = computed(() => {
    return this.rotasLoaded() === this.dataService.predictionRotaWeeks
            .filter(week => week.rotaExist)
            .length;
  });

  /**
   * Increments a number of how many rotas are already loaded on the page.
   * @param isLoaded it indicates if a rota is already loaded.
   */
  incrementRotasLoaded(isLoaded: boolean): void {
    if (isLoaded) {
      this.rotasLoaded.update(count => count + 1);
    }
  }

  /**
   * Converts HTML data into a PDF, downloads it.
   */
  openPDF(): void {
    const weeks = this.getWeeks();
    const pdfName = this.pdfName(weeks);
    this.pdfService.getPDF(weeks).then(pdfData => {
      pdfData.save(pdfName);
    });
  }

  /**
   * Converts HTML data into a PDF, sends an email with that PDF.
   */
  sendEmail(): void {
    const weeks = this.getWeeks();
    const pdfName = this.pdfName(weeks);
    this.pdfService.getPDF(weeks).then(pdfData => {
      EmailFormComponent.open(this.modalService, {
        base64EncodedPdf: pdfData.output('datauristring'),
        pdfName: pdfName
      });
    });
  }

  private getWeeks(): PredictionRotaWeek[] {
    const weeks = [];
    this.dataService.predictionRotaWeeks.forEach(week => {
      if (week.rotaExist) {
        weeks.push(week);
      }
    });
    return weeks;
  }

  private pdfName(weeks: PredictionRotaWeek[]): string {
    const firstWeek = moment(weeks[0].from).format('WW');
    const lastWeek = moment(weeks[weeks.length - 1].from).format('WW');
    return 'Week ' + firstWeek + ' - Week ' + lastWeek + ' Rotas.pdf';
  }
}